<template>
    <button
        v-show="!hidden"
        class="pb-chat-facade"
        type="button"
        :aria-label="ARIA_LABEL"
        :disabled="loading"
        @click="onClick"
    >
        <img :src="AVATAR" alt="" width="60" height="60" loading="lazy" decoding="async" />
    </button>
</template>

<script setup lang="ts">
/**
 * The facade that opens support chat.
 *
 * The SDK renders no launcher until `openChat()` runs, so this button is both
 * what a person clicks and what pulls the ~85KB bundle down. Loading on first
 * interaction rather than on every page keeps an app's initial payload free of
 * chat JS until someone asks for it.
 *
 * Once the SDK is up it renders its own launcher and panel, so this facade
 * retires itself — two bubbles in the corner would be worse than none. The
 * hand-off flag is shared, because chat opens from other places too, and from
 * that point a route rule has to be enforced against the SDK's UI rather than
 * against this button.
 *
 * WHY THE ROUTE RULES ARE PROPS
 * Which pages reserve the corner, and which hand the widget to something else
 * entirely, is a fact about one app's pages — this component cannot know it and
 * must not branch on which app it is in. It takes the two answers and applies
 * them the same way everywhere.
 */
import { computed, onMounted, ref, watch } from 'vue';
import type { ChatLauncherApi } from './types';
import { setSdkHostVisible } from './sdk';

const AVATAR = 'https://cdn.privacybee.com/chat/avatar-bee.webp';
const ARIA_LABEL = 'Chat with us';

const props = withDefaults(
    defineProps<{
        /** The app's bound widget — `defineChatWidget(...)`. */
        chat: ChatLauncherApi;
        /**
         * The SDK's own UI is unwelcome here, not merely this facade: the
         * button hides AND a widget already up is suppressed, which shuts the
         * panel and disarms auto-open rather than just hiding a box.
         */
        suppressed?: boolean;
        /**
         * Chat is deferred here — something else owns it on this page. The
         * facade stands down and no conversation is restored until it is false
         * again, so mounting here defers the restore rather than skipping it.
         */
        deferred?: boolean;
    }>(),
    { suppressed: false, deferred: false },
);

// Shared: chat also opens from elsewhere in the app, and this facade has to
// stand down for that too.
const sdkOwnsLauncher = props.chat.useSdkOwnsLauncher();
const loading = ref(false);

const hidden = computed(() => sdkOwnsLauncher.value || props.suppressed || props.deferred);

/**
 * Keep the SDK's own UI off the pages that reserve the corner after a
 * client-side navigation. Without this someone who opens chat on a normal page
 * carries the launcher — and an open panel — onto the very pages whose corner
 * is spoken for.
 *
 * The SDK mounts visible, so its host already agrees until this launcher says
 * otherwise; tracking what was last pushed keeps an app with no route rules at
 * all from telling a freshly-opened widget to do what it is already doing.
 */
let sdkHostVisible = true;
function syncSdkVisibility(): void {
    if (!sdkOwnsLauncher.value) return;
    const visible = !props.suppressed;
    if (visible === sdkHostVisible) return;

    sdkHostVisible = visible;
    setSdkHostVisible(visible);
}

// Both sources, because either can be what puts the SDK on a reserved page: a
// navigation while it is already up, or a bundle that finishes loading after
// the member has already navigated away from where they clicked.
watch([() => props.suppressed, sdkOwnsLauncher], syncSdkVisibility);

/**
 * Someone who was mid-conversation before this page load has no socket until
 * something opens one, so a reply landing now would reach nothing. Restore for
 * them — and only them — so a first-time visitor still pays no bundle.
 */
function maybeRestore(): void {
    if (props.deferred || sdkOwnsLauncher.value) return;
    if (props.chat.hasExistingConversation()) void props.chat.restoreSupportChat();
}

onMounted(maybeRestore);

// This component outlives every client-side navigation, so mounting on a page
// that defers chat is not a decision to skip the restore forever — only to hold
// it until the member is somewhere it is allowed to happen.
watch(() => props.deferred, (nowDeferred) => {
    if (!nowDeferred) maybeRestore();
});

async function onClick(): Promise<void> {
    if (loading.value) return;
    loading.value = true;
    try {
        await props.chat.openSupportChat();
    } catch {
        // Leave the facade up so the member can try again; the loader clears
        // its cached promise on failure so the retry refetches.
    } finally {
        loading.value = false;
    }
}
</script>

<style scoped>
/* Mirrors the SDK's own .pb-launcher so the facade → live-SDK handoff is
   visually seamless: transparent button, soft shadow, the 120x120 avatar
   filling a 60px circle.

   No `:focus-visible` rule here on purpose. The ring that clears 3:1 depends on
   the grounds one app paints, and `.pb-chat-facade` is the stable class an app
   writes its own against — a rule here would be the one it has to fight. */
.pb-chat-facade {
    position: fixed;
    right: 20px;
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    z-index: 2147483646;
    width: 60px;
    height: 60px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(0, 0, 0, 0.18);
    cursor: pointer;
    display: block;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.22s ease;
}

.pb-chat-facade img {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 50%;
    object-fit: cover;
}

.pb-chat-facade:disabled {
    opacity: 0.7;
    pointer-events: none;
}

@media (hover: hover) {
    .pb-chat-facade:hover {
        transform: scale(1.06);
        box-shadow: 0 14px 30px rgba(0, 0, 0, 0.28), 0 3px 8px rgba(0, 0, 0, 0.22);
    }
}

.pb-chat-facade:active {
    transform: scale(0.96);
}

@media (prefers-reduced-motion: reduce) {
    .pb-chat-facade {
        transition: none;
    }
}
</style>
